import { BaseClient } from '../base-client';
import type { CreateSkillInput, ListSkillsQuery, PatchSkillInput, Skill } from './types';

type One = { data: Skill };
type ListResponse = { data: Skill[]; meta: { total: number; limit: number; offset: number } };

export class SkillsClient extends BaseClient {
  list(query: ListSkillsQuery = {}): Promise<ListResponse> {
    return this.request('/api/skills', { query: normalize(query) });
  }

  /** Batch fetch skills by exactly the referenced ids — bounded, never
   *  capped. See `BaseClient.findByIdsAt`. */
  findByIds(ids: readonly string[]): Promise<ListResponse> {
    return this.findByIdsAt<Skill>('/api/skills/find', ids);
  }

  get(id: string): Promise<One> {
    return this.request(`/api/skills/${encodeURIComponent(id)}`);
  }

  create(input: CreateSkillInput): Promise<One> {
    return this.request('/api/skills', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchSkillInput): Promise<One> {
    return this.request(`/api/skills/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/skills/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  restore(id: string): Promise<One> {
    return this.request(`/api/skills/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
